import React, { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import BottomNavTray from '../components/MobileNavigation/BottomNavTray';
import { MobileChromeProvider, useMobileChrome } from '../contexts/MobileChromeContext';
import { useTheme } from '../contexts/ThemeContext';
import useIsMobile from '../hooks/useIsMobile';
import { useMobileBottomNavNavigate } from '../hooks/useMobileBottomNavNavigate';
import { getMobileBottomNavActivePage } from '../utils/dashboardMobileNav';
import { preloadTradePage } from '../routes/tradePageLoad';

function MobileShellFallback() {
  const { colors } = useTheme();
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        background: colors?.bgPrimary || '#0b1118',
      }}
      aria-busy="true"
      aria-label="Loading page"
    />
  );
}

/**
 * Persistent chrome for /trade + /dashboard on mobile.
 * Keeps BottomNavTray mounted across tab switches; Suspense is scoped to <Outlet />
 * so lazy route chunks do not unmount the tray/header shell.
 */
function MobileShellFrame() {
  const { colors } = useTheme();
  const location = useLocation();
  const navigateBottomTab = useMobileBottomNavNavigate();
  const { bottomNavHidden } = useMobileChrome();
  const activePage = getMobileBottomNavActivePage(location.pathname);

  useEffect(() => {
    document.body.classList.add('trade-page-mobile');
    document.documentElement.classList.add('trade-page-mobile');
    return () => {
      document.body.classList.remove('trade-page-mobile');
      document.documentElement.classList.remove('trade-page-mobile');
    };
  }, []);

  // Warm the Trade chunk while the user is on any bottom-nav screen so the first
  // Trade tap does not hit chunk Suspense (chrome flash).
  useEffect(() => {
    let cancelled = false;
    const idle = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback
      : (cb) => setTimeout(cb, 300);
    const cancelIdle = typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function'
      ? window.cancelIdleCallback
      : clearTimeout;
    const id = idle(() => {
      if (!cancelled) preloadTradePage().catch(() => {});
    });
    return () => {
      cancelled = true;
      cancelIdle(id);
    };
  }, []);

  return (
    <div
      className="text-light"
      style={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: colors.bgPrimary,
        overflow: 'hidden',
      }}
    >
      <main style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        <Suspense fallback={<MobileShellFallback />}>
          <Outlet />
        </Suspense>
      </main>

      {!bottomNavHidden && (
        <BottomNavTray
          activePage={activePage}
          onPageChange={navigateBottomTab}
        />
      )}
    </div>
  );
}

export default function AuthenticatedMobileShell() {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return (
      <Suspense fallback={<MobileShellFallback />}>
        <Outlet />
      </Suspense>
    );
  }

  return (
    <MobileChromeProvider>
      <MobileShellFrame />
    </MobileChromeProvider>
  );
}
